import React from 'react';
import history from 'helpers/history';
import {
    Car,
    Edit3,
    Trash2,
    ArrowUpDown,
    ArrowDown,
    ArrowUp,
    Eye,
    MessageCircle,
    User as UserIcon,
} from 'lucide-react';

import VehicleModel from 'models/VehicleModel';
import TableColumn, { TableColumOrderStatus } from 'models/TableColumnModel';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import { deleteVehicle } from 'actions/vehicleActions';
import { connect } from 'react-redux';
import HoverTooltip from './HoverTooltip';
import InternalChatModal from 'components/modals/InternalChatModal';
import InternalChatSubject from 'models/types/InternalChatSubjectType';

interface State {
    isInternalChatModelVisible?: boolean;
    selectedVehicle?: VehicleModel;
}

interface Props {
    dispatch: any;
    columns: TableColumn[];
    vehicles: VehicleModel[];
    onEdit: (vehicleId: string) => void;
    onOrder: Function;
    isLoading?: boolean;
    hideColumns?: string[];
}

class VehiclesTable extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isInternalChatModelVisible: false,
        };
    }

    isColumnActive(columnName: string) {
        return this.props.columns.find((column) => column.name === columnName)?.active;
    }

    handleDelete(vehicle: VehicleModel) {
        displayConfirmationModal(`${vehicle.make} ${vehicle.model} ${vehicle.VIN ? `(${vehicle.VIN})` : ''}`)
            .then(() => {
                this.props.dispatch(deleteVehicle(String(vehicle.id)));
            })
            .catch((_) => _);
    }

    openInternalChat(vehicle: VehicleModel) {
        this.setState({ isInternalChatModelVisible: true, selectedVehicle: vehicle });
    }
    render() {
        const { vehicles, columns, isLoading, onEdit } = this.props;
        const { isInternalChatModelVisible, selectedVehicle } = this.state;
        return (
            <div className="overflow-x-auto">
                {isInternalChatModelVisible && (
                    <InternalChatModal
                        chatId={selectedVehicle!.internalChatId!}
                        subject={InternalChatSubject.Vehicle}
                        subjectId={selectedVehicle?.id!}
                        onClose={() => {
                            this.setState({ isInternalChatModelVisible: false, selectedVehicle: undefined });
                        }}
                    />
                )}
                <table className="min-w-full">
                    <thead className="bg-gray-50">
                        <tr className="bg-blue-500 text-white">
                            {columns.map(
                                (column, index) =>
                                    column.active && (
                                        <th
                                            key={index}
                                            className="px-6 py-4 text-left text-xs font-semibold  uppercase tracking-wider"
                                        >
                                            <div
                                                className="flex items-center space-x-1  transition-colors clickable"
                                                onClick={() => column.sortable && this.props.onOrder(column.name)}
                                            >
                                                <span>{column.text}</span>
                                                {column.sortable && (
                                                    <span className="ml-1">
                                                        {column?.orderStatus === TableColumOrderStatus.None ? (
                                                            <ArrowUpDown className="h-3 w-3" />
                                                        ) : column?.orderStatus === TableColumOrderStatus.Ascending ? (
                                                            <ArrowDown className="h-3 w-3" />
                                                        ) : (
                                                            <ArrowUp className="h-3 w-3" />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ),
                            )}
                            <th className="px-6 py-4 text-right text-xs font-semibold  uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                        {vehicles &&
                            vehicles.map((vehicle, index) => (
                                <tr
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    key={index}
                                    onClick={() => {
                                        history.push(`/vehicle/${vehicle.id}`);
                                    }}
                                >
                                    {this.isColumnActive('model') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Car className="h-4 w-4 text-gray-400 mr-3" />
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {vehicle.model}
                                                </span>
                                            </div>
                                        </td>
                                    )}
                                    {this.isColumnActive('make') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900 font-semibold">{vehicle.make}</span>
                                        </td>
                                    )}
                                    {this.isColumnActive('series') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900 font-semibold">
                                                {vehicle.series}
                                            </span>
                                        </td>
                                    )}
                                    {this.isColumnActive('year') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900 font-semibold">{vehicle.year}</span>
                                        </td>
                                    )}
                                    {this.isColumnActive('licensePlate') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900 font-semibold">
                                                {vehicle.licensePlate}
                                            </span>
                                        </td>
                                    )}
                                    {this.isColumnActive('VIN') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm font-mono text-gray-900 font-semibold">
                                                {vehicle.VIN}
                                            </span>
                                        </td>
                                    )}

                                    {this.isColumnActive('notes') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {vehicle.notes?.length && (
                                                <HoverTooltip
                                                    content={
                                                        <div className=" p-4 rounded-lg max-w-xs">
                                                            <div className="text-sm">{vehicle.notes}</div>
                                                        </div>
                                                    }
                                                >
                                                    <MessageCircle
                                                        className="h-4 w-4 text-green-500 hover:text-green-700 cursor-pointer"
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation();
                                                            this.openInternalChat(vehicle);
                                                        }}
                                                    />
                                                </HoverTooltip>
                                            )}
                                            {vehicle.internalChatId && (
                                                <MessageCircle
                                                    className="h-4 w-4 text-red-500 hover:text-red-700 cursor-pointer"
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        this.openInternalChat(vehicle);
                                                    }}
                                                />
                                            )}
                                        </td>
                                    )}
                                    {this.isColumnActive('customer') && (
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    history.push(`/customer/${vehicle.customer?.id}`);
                                                }}
                                            >
                                                <UserIcon className="h-3 w-3" />
                                                <span>
                                                    {vehicle.customer?.firstName} {vehicle.customer?.lastName}
                                                </span>
                                            </button>
                                        </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-2">
                                            <button
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    history.push(`/vehicle/${vehicle.id}`);
                                                }}
                                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                title="View vehicle"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    onEdit(vehicle.id!);
                                                }}
                                                className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                title="Edit vehicle"
                                            >
                                                <Edit3 className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    this.handleDelete(vehicle);
                                                }}
                                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Delete vehicle"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                {(!vehicles || vehicles.length === 0) && !isLoading && (
                    <div className="py-12 text-center border-t border-gray-200">
                        <Car className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
                        <p className="text-gray-500">Try adjusting your search criteria or add a new vehicle.</p>
                    </div>
                )}
                {isLoading && (
                    <div className="py-12 text-center border-t border-gray-200">
                        <div className="text-lg font-medium text-gray-900">Loading vehicles...</div>
                    </div>
                )}
            </div>
        );
    }
}

export default connect()(VehiclesTable);
